import { BaseLoggingService, LogType } from "@obg/ngx.global.evo";
import * as Sentry from "@sentry/browser";
import { Integrations } from "@sentry/tracing";
import { Transaction, TransactionContext } from "@sentry/types";
import { isNil, isObject } from "lodash-es";

import { SentryConfig } from "./sentry-logging.model";

export class SentryLoggingProvider implements BaseLoggingService {
	private defaults: Sentry.BrowserOptions = {
		beforeBreadcrumb: (breadCrumb: Sentry.Breadcrumb) =>
			breadCrumb.level === Sentry.Severity.Log ? null : breadCrumb,
	};

	constructor(private config: SentryConfig) {}

	configureGlobalTags(tags: Record<string, string>) {
		Sentry.configureScope((scope) => {
			scope.setTags(tags);
		});
	}

	log(type: LogType, message: string | Error, data?: any) {
		if ((type === LogType.Debug || type === LogType.Info || type === LogType.Warn) && this.config.isProd) {
			return;
		}

		Sentry.withScope((scope) => {
			this.setLevelAndExtrasForScope(data, scope, type);
			message instanceof Error ? Sentry.captureException(message) : Sentry.captureMessage(message);
		});
	}

	initialize() {
		const configs = {
			...this.defaults,
			dsn: this.config.url,
			release: this.config.version,
			environment: this.config.environment,
			transport: this.config.transport,
			ignoreErrors: this.config.ignoredErrors,
			beforeSend: this.config.beforeSend,
			integrations: [new Integrations.BrowserTracing()],
			sampleRate: this.config.sampleRate,
		};

		// this enables Performance Monitoring
		if (this.config.isPerfMonitoringEnabled) {
			configs.tracesSampleRate = this.config.isProd ? 0.1 : 0.4;
		}

		Sentry.init(configs);
	}

	startTransaction(context: TransactionContext): Transaction {
		return Sentry.startTransaction(context);
	}

	private setLevelAndExtrasForScope(data: any, scope: Sentry.Scope, level: LogType) {
		const severity = this.mapLogTypeToSeverity(level);
		scope.setLevel(severity);

		if (isNil(data)) {
			return;
		}

		if (!isObject(data)) {
			scope.setExtra("details", data);
			return;
		}

		const dataKeys = Object.entries(data);
		dataKeys.forEach(([key, value]) => {
			scope.setExtra(key, value);
		});
	}

	private mapLogTypeToSeverity(level: LogType): Sentry.Severity {
		switch (level) {
			case LogType.Debug:
				return Sentry.Severity.Debug;
			case LogType.Error:
				return Sentry.Severity.Error;
			case LogType.Info:
				return Sentry.Severity.Info;
			default:
				return Sentry.Severity.Warning;
		}
	}
}
